import { HttpClient, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Platform } from '@ionic/angular';
import { AmplifyService } from 'aws-amplify-angular';
import { from, Observable, ReplaySubject } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { v4 as uuid} from 'uuid';
import { ApplicationState } from '../types/ApplicationState';
// import { BackendError } from '../types/BackendError';
import { oneLine } from '../utils/one-line-converter';
import { LoadingService } from './loading.service';
import * as moment from 'moment';
import { NetworkConnectivityService } from './network-connectivity.service';
import { Capacitor } from '@capacitor/core';
import { Environment } from '../../environments/environment';

/*
  Provider to hold auth logic for AWS Amplify
  https://github.com/aws-amplify/amplify-js/tree/master/packages/aws-amplify-angular/src/components/authenticator
*/

interface ICognitoSignUpData {
  phone_number: string;
  password: string;
  given_name: string;
  family_name: string;
  birthdate: string;
  gender: string;
  'custom:sitecode': string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(public http: HttpClient,
    private amplifyService: AmplifyService,
    public loadingService: LoadingService,
    private network: NetworkConnectivityService
  ) { }

  static AUTH_ERROR: any /* BackendError */ = {
    status: 403,
    statusText: 'Not Logged In',
    message: 'You must be logged in to perform this action'
  };
  currentUser: any;
  authenticatedUser!: Promise<any>;

  singleUseRegistrationPassword!: string;

  public loggedIn: ReplaySubject<any> = new ReplaySubject(1);

  readonly SYNC_ON_LOGOUT_TIMEOUT = 10000;

  async login(username: string, password: string): Promise<any> {
    try {
      if (Capacitor.isNativePlatform()) {
        this.currentUser = await this.amplifyService.auth().signIn(username, password);
      } else {
        this.currentUser = await this.amplifyService.auth().signIn({username});
      }
    } catch (error: any) {
      if (this.isInternalErrorException(error)) {
        error.message = oneLine`Sorry, we can't log you in
        right now due to an unavailable service. We're
        aware of the problem and working on a solution.
        Please try again later.`;
      }
      throw error;
    }
  }

  async isApproved(): Promise<ApplicationState> {
    const user = await this.getCurrentUser();
    const jwtClaims: any = user.getSignInUserSession().getIdToken().decodePayload() || {};
    const groups = jwtClaims['cognito:groups'] || [];
    return (groups.length > 0 && groups.indexOf('public') === -1) ? ApplicationState.APPROVED : ApplicationState.AUTHENTICATED;
  }

  async getCurrentUser(): Promise<any> {
    try {
      if (!this.authenticatedUser) {
        this.authenticatedUser = this.amplifyService.auth().currentAuthenticatedUser();
      }
      const current = await this.authenticatedUser;
      if (current) {
        const jwtClaims: any = current.getSignInUserSession().getAccessToken().decodePayload() || {};
        const tokenIssuedAt: number = current.getSignInUserSession().getAccessToken().getIssuedAt();
        const expiration: number = current.getSignInUserSession().getAccessToken().getExpiration();

        // Force a refresh when the token is older than a day
        const forceRefresh = moment.unix(tokenIssuedAt).add(1, 'days').valueOf() <= moment().valueOf() && this.network.isOnline();
        const isExpired = moment.unix(expiration).valueOf() < moment().valueOf();
        const isApproved = (jwtClaims['cognito:groups'] || []).indexOf('Registered') === -1 && (jwtClaims['cognito:groups'] || []).indexOf('public') === -1;
        if ((!this.network.isOnline() || current.getSignInUserSession().isValid()) && isApproved && !forceRefresh && !isExpired) {
          return current;
        } else {
          await this.forceRefreshToken();
        }
      }
    } catch (err: any) {
      console.log('Not authenticated yet, waiting', `sss${err}sss`, err.message);
    }
    this.authenticatedUser = this.amplifyService.auth().currentAuthenticatedUser();
    return this.authenticatedUser;
  }

  async forceRefreshToken() {
    try {
      const auth = await this.amplifyService.auth();
      const currentSession = await auth.currentSession();
      const user = await auth.currentAuthenticatedUser();
      const refreshToken = await currentSession.getRefreshToken();
      // Unable to use await on refreshSession as relies on callback
      // public refreshSession(refreshToken: CognitoRefreshToken, callback: NodeCallback<any, any>): void;
      return new Promise<void>((res, rej) => {
        user.refreshSession(refreshToken, async (error: any) => {
          if (error) {
            console.error('refreshSession: error', error);
            rej();
          } else {
            res();
          }
          // console.debug('new session', (await this.amplifyService.auth().currentAuthenticatedUser()).getSignInUserSession());
        });
      });
    } catch (error) {
      throw error;
    }
  }

  getCurrentSession(): Promise<any> {
    return this.amplifyService.auth().currentSession();
  }

  async logout() {
    // this.waitForSyncDone()
    //   .then(() => {
    //     this.purgeRecords()
    //       .then(() => {
    this.amplifyService.auth().signOut();
    //       });
    //   });
  }

  generateNewPassword(): string {
    return uuid();
  }

  registerUser(userData: ICognitoSignUpData): Promise<any> {
    this.singleUseRegistrationPassword = userData.password;
    return this.amplifyService.auth()
      .signUp({
        username: userData.phone_number,
        password: userData.password,
        attributes: {
          phone_number: userData.phone_number,
          given_name: userData.given_name,
          family_name: userData.family_name,
          birthdate: userData.birthdate,
          gender: userData.gender,
          'custom:sitecode': userData['custom:sitecode']
        }
      });
  }

  verifySignUpCode(phone: string, code: string): Promise<any> {
    // tslint:disable-next-line:max-line-length
    // https://github.com/aws-amplify/amplify-js/blob/master/packages/aws-amplify-angular/src/components/authenticator/confirm-sign-up-component/confirm-sign-up.component.core.ts
    return this.amplifyService.auth()
      .confirmSignUp(phone, code)
      .then(() => {
        this.login(phone, this.singleUseRegistrationPassword);
      } );
  }

  resendSignUpCode(phone: string): Promise<any> {
    // tslint:disable-next-line:max-line-length
    // https://github.com/aws-amplify/amplify-js/blob/master/packages/aws-amplify-angular/src/components/authenticator/confirm-sign-up-component/confirm-sign-up.component.core.ts
    return this.amplifyService.auth()
      .resendSignUp(phone)
      .then(() => this.login(phone, this.singleUseRegistrationPassword));
  }

  sendForgotPasswordCode(phone: string): Promise<any> {
    return this.amplifyService.auth().forgotPassword(phone);
  }

  async sendSMSCode(phone: string): Promise<any> {
    this.amplifyService.auth().signIn({username: phone})
      .then((user: any) => {
        this.currentUser = user;
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  submitVerifySMSCode(code: string, user: any): Promise<any> {
    return this.amplifyService.auth().sendCustomChallengeAnswer(this.currentUser, code);
  }

  submitForgotPassword(phone: string, code: string, newPassword: string): Promise<any> {
    return this.amplifyService.auth()
      .forgotPasswordSubmit(
        phone,
        code,
        newPassword
      );
  }

  // https://github.com/aws-amplify/amplify-js/blob/master/docs/media/authentication_guide.md
  // https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmForgotPassword.html

  isUserNotFoundException(error: any) {
    return error.code === 'UserNotFoundException';
  }

  isNotAuthorizedException(error: any) {
    return error.code === 'NotAuthorizedException';
  }

  isUserNotConfirmedException(error: any) {
    return error.code === 'UserNotConfirmedException';
  }

  isUsernameExistsException(error: any) {
    return error.code === 'UsernameExistsException';
  }

  // Invalid code errors
  isCodeMismatchException(error: any) {
    return error.code === 'CodeMismatchException';
  }

  isExpiredCodeException(error: any) {
    return error.code === 'ExpiredCodeException';
  }

  // Serverside Errors start
  isInvalidParameterException(error: any) {
    return error.code === 'InvalidParameterException';
  }

  isUserLambdaValidationException(error: any) {
    return error.code === 'UserLambdaValidationException';
  }

  isUnexpectedLambdaException(error: any) {
    return error.code === 'UnexpectedLambdaException';
  }

  isInvalidLambdaResponseException(error: any) {
    return error.code === 'InvalidLambdaResponseException';
  }

  isInternalErrorException(error: any) {
    return error.code === 'InternalErrorException';
  }

  isTooManyRequestsException(error: any) {
    return error.code === 'TooManyRequestsException';
  }

  isTooManyFailedAttemptsException(error: any) {
    return error.code === 'TooManyFailedAttemptsException';
  }

  isLimitExceededException(error: any) {
    return error.code === 'LimitExceededException';
  }
  // Serverside Errors end
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  amplifyAppService: AuthService;
  platform: Platform;
  
  constructor(inj: Injector) {
    this.amplifyAppService = inj.get(AuthService);
    this.platform = inj.get(Platform);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (request.url.startsWith(Environment.SYNC_URL) || request.url.startsWith(Environment.MOBILE_API)) {

      return from(this.amplifyAppService.getCurrentSession())
        .pipe(
          mergeMap(tokens => {
            const headers = {
              Authorization: `Bearer ${tokens['idToken'].jwtToken}`
            };
            request = request.clone({
              setHeaders: headers
            });
            return next.handle(request);
          }),
          catchError(error => {
            // We want only want to track errors specific to the interceptor here
            if (!(error instanceof HttpErrorResponse)) {
              if (!this.platform.is('pwa')) {
                // this.firebaseAnalytics.logEvent('http_interceptor_error', { error });
              }
              console.error(error);
            }
            throw error;
          }
        )
      );
    } else {
      return next.handle(request);
    }
  }
}
